import React, { useState } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Button, Card, CardBody, CardHeader, Chip, Spinner } from "@heroui/react";
import { MagnifyingGlassIcon, DocumentArrowDownIcon, ArrowUpIcon, ArrowDownIcon, ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import { ListCommanWay } from "API/commonApi";

const Last6MonthsReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(moment().subtract(6, "months").startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<string>(moment().endOf("day").format("YYYY-MM-DD"));
  const [tableData, setTableData] = useState<any[]>([]);
  const [monthHeaders, setMonthHeaders] = useState<string[]>([]);
  const [sortStat, setSortStat] = useState<any>({ m1: "desc" }); // Default sort by M1 descending
  const [query, setQuery] = useState<any>({
    startDate: moment().subtract(6, "months").startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("day").format("YYYY-MM-DD"),
  });

  const { isFetching, isLoading, refetch } = useQuery(
    ["last_6_months_report", query],
    () => ListCommanWay("/admin/booking/6monthly-summary", { ...query }, {}),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status === "SUCCESS" && data.data) {
          // Get unique months sorted
          const uniqueMonths = [...new Set(data.data.map((item: any) => item.month))].sort() as string[];
          setMonthHeaders(uniqueMonths.map((m: string) => moment(m, "YYYY-MM").format("MMM YY")));

          // Group data by code and pivot months
          const customerMap: Record<string, any> = {};
          data.data.forEach((item: any) => {
            if (!customerMap[item.code]) {
              customerMap[item.code] = {
                code: item.code,
                sh: item.sh,
                m1: 0, m2: 0, m3: 0, m4: 0, m5: 0, m6: 0,
              };
            }
            const monthIndex = uniqueMonths.indexOf(item.month);
            if (monthIndex >= 0 && monthIndex < 6) {
              customerMap[item.code][`m${monthIndex + 1}`] = Number(item.revenue) || 0;
            }
          });

          // Default sort by M1 descending (high to low)
          const sortedData = Object.values(customerMap).sort((a: any, b: any) => b.m1 - a.m1);
          setTableData(sortedData);
          setSortStat({ m1: "desc" });
        } else {
          setTableData([]);
        }
      },
      onError: () => {
        setTableData([]);
      },
    }
  );

  const handleSort = (key: string) => {
    let data = [...tableData];
    let order = sortStat[key] === "desc" ? "asc" : "desc";

    const sortedData = data.sort((a, b) => {
      const valueA = a[key] || "";
      const valueB = b[key] || "";

      if (typeof valueA === "number" && typeof valueB === "number") {
        return order === "asc" ? valueA - valueB : valueB - valueA;
      }
      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortStat({ [key]: order });
    setTableData(sortedData);
  };

  const GET_SORT_ICON = (key: string) => {
    switch (sortStat[key]) {
      case "asc":
        return <ArrowUpIcon className="h-3 w-3 inline-block ml-1" />;
      case "desc":
        return <ArrowDownIcon className="h-3 w-3 inline-block ml-1" />;
      default:
        return <ArrowsUpDownIcon className="h-3 w-3 inline-block ml-1 opacity-40" />;
    }
  };

  const handleSubmit = () => {
    setQuery({
      startDate: startDate,
      endDate: endDate,
    });
  };

  const exportToExcel = () => {
    const exportData = tableData.map((row, index) => ({
      "Sr.": index + 1,
      "Customer Code": row.code || "CASH",
      "Customer Name": row.sh || "CASH",
      [monthHeaders[0] || "M1"]: row.m1 || 0,
      [monthHeaders[1] || "M2"]: row.m2 || 0,
      [monthHeaders[2] || "M3"]: row.m3 || 0,
      [monthHeaders[3] || "M4"]: row.m4 || 0,
      [monthHeaders[4] || "M5"]: row.m5 || 0,
      [monthHeaders[5] || "M6"]: row.m6 || 0,
    }));
    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Last 6 Months Report");
    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "Last6MonthsReport.xlsx");
  };

  const getTotals = () => {
    return tableData.reduce(
      (acc, row) => ({
        m1: acc.m1 + (row.m1 || 0),
        m2: acc.m2 + (row.m2 || 0),
        m3: acc.m3 + (row.m3 || 0),
        m4: acc.m4 + (row.m4 || 0),
        m5: acc.m5 + (row.m5 || 0),
        m6: acc.m6 + (row.m6 || 0),
      }),
      { m1: 0, m2: 0, m3: 0, m4: 0, m5: 0, m6: 0 }
    );
  };

  const totals = getTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-inter">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 via-indigo-600 to-indigo-700 px-6 pt-2 pb-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Last 6 Months Report</h1>
              <p className="text-white/60 text-xs">-</p>
            </div>
          </div>
          
        </div>
      </div>

      {/* Filters Card */}
      <div className="px-4 -mt-4">
        <Card className="shadow-xl border-none overflow-visible">
          <CardBody className="p-4 overflow-visible hidden">
            <div className="flex flex-wrap items-end gap-3">
              <AfDateRangePicker
                label="Date Range"
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />

              <Button
                size="sm"
                variant="flat"
                color="primary"
                className="h-8 mt-5"
                startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
                onPress={exportToExcel}
              >
                Export
              </Button>
              <Button
                size="sm"
                color="success"
                className="h-8 mt-5"
                onPress={handleSubmit}
                isLoading={isLoading || isFetching}
                startContent={!isLoading && !isFetching && <MagnifyingGlassIcon className="w-4 h-4" />}
              >
                Search
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Results Table */}
      <div className="px-4 mt- pb-8">
        <Card className="shadow-xl border-none overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </span>
              <h2 className="text-lg font-bold text-slate-800">Monthly Summary</h2>
              {tableData.length > 0 && (
                <Chip size="sm" variant="flat" color="primary" className="ml-2">
                  {tableData.length} records
                </Chip>
              )}
            </div>
          </CardHeader>
          <CardBody className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200">Sr.</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("code")}>Code {GET_SORT_ICON("code")}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("sh")}>Customer Name {GET_SORT_ICON("sh")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("m1")}>{monthHeaders[0] || "M1"} {GET_SORT_ICON("m1")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("m2")}>{monthHeaders[1] || "M2"} {GET_SORT_ICON("m2")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("m3")}>{monthHeaders[2] || "M3"} {GET_SORT_ICON("m3")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("m4")}>{monthHeaders[3] || "M4"} {GET_SORT_ICON("m4")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("m5")}>{monthHeaders[4] || "M5"} {GET_SORT_ICON("m5")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("m6")}>{monthHeaders[5] || "M6"} {GET_SORT_ICON("m6")}</th>
                </tr>
              </thead>
              <tbody>
                {(isLoading || isFetching) && (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <Spinner size="lg" color="primary" />
                      <p className="text-slate-500 mt-2">Loading data...</p>
                    </td>
                  </tr>
                )}
                {!isLoading && !isFetching && !tableData.length && (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <div className="flex flex-col items-center">
                        <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <p className="text-slate-500 font-medium">No data found</p>
                        <p className="text-slate-400 text-sm">Try adjusting your date range</p>
                      </div>
                    </td>
                  </tr>
                )}
                {tableData.map((row, index) => (
                  <tr key={row.id || index} className="hover:bg-slate-50 transition-colors border-b border-slate-200">
                    <td className="px-4 py-3 text-slate-500 border-r border-slate-100">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 border-r border-slate-100">{row.code || "CASH"}</td>
                    <td className="px-4 py-3 text-slate-700 border-r border-slate-100">{row.sh || "CASH"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700 border-r border-slate-100 font-mono text-base"> {Math.round(row.m1 || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700 border-r border-slate-100 font-mono text-base"> {Math.round(row.m2 || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700 border-r border-slate-100 font-mono text-base"> {Math.round(row.m3 || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700 border-r border-slate-100 font-mono text-base"> {Math.round(row.m4 || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700 border-r border-slate-100 font-mono text-base"> {Math.round(row.m5 || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700 border-r border-slate-100 font-mono text-base"> {Math.round(row.m6 || 0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
                {tableData.length > 0 && (
                  <tr className="bg-slate-100 font-bold">
                    <td className="px-4 py-3 border-r border-slate-200" colSpan={3}>Total</td>
                    <td className="px-4 py-3 text-right border-r border-slate-200"> {Math.round(totals.m1).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right border-r border-slate-200"> {Math.round(totals.m2).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right border-r border-slate-200"> {Math.round(totals.m3).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right border-r border-slate-200"> {Math.round(totals.m4).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right border-r border-slate-200"> {Math.round(totals.m5).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right"> {Math.round(totals.m6).toLocaleString('en-IN')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default Last6MonthsReport;
